import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Plus, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router';
import { ClientMark } from '@/features/clients/client-mark';
import { ClientFormDialog } from '@/features/clients/client-form';
import { Button } from '@/design-system/components/button';
import { Skeleton } from '@/design-system/components/skeleton';
import { Tooltip } from '@/design-system/components/tooltip';
import { api } from '@/lib/api';
import { useClients } from '@/lib/clients';
import { useCan } from '@/lib/session';
import { cn } from '@/lib/utils';

/** Keeps the client selected while a project or Creative editor is open, including old links. */
export function useNavigationClient() {
  const { pathname } = useLocation();
  const direct = pathname.match(/^\/clients\/([^/]+)/)?.[1];
  const project = pathname.match(/^\/projects\/p\/([^/]+)/)?.[1];
  const creative = pathname.match(/^\/creative\/(boards|notes|storyboards)\/([^/]+)/);
  const resource = project ? `/projects/${project}` : creative ? `/${creative[1]}/${creative[2]}` : '';
  const query = useQuery({
    queryKey: ['client-navigation', resource],
    queryFn: () =>
      api.get<{
        client?: { id: string } | null;
        clientId?: string | null;
        board?: { clientId: string | null };
        storyboard?: { clientId: string | null };
      }>(resource),
    enabled: Boolean(resource),
  });
  const data = query.data;
  return {
    clientId:
      direct ?? data?.client?.id ?? data?.clientId ?? data?.board?.clientId ?? data?.storyboard?.clientId,
    section: creative ? 'creative' : project ? 'projects' : pathname.split('/')[3],
  };
}

export function ClientsNavigation({ compact, onNavigate }: { compact: boolean; onNavigate?: () => void }) {
  const clients = useClients();
  const canRead = useCan('clients:read');
  const canWrite = useCan('clients:write');
  const canInvite = useCan('clients:invite');
  const canCreative = useCan('creative:read');
  const [creating, setCreating] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const active = useNavigationClient();
  const counts = useQuery({
    queryKey: ['notifications', 'clients-unread'],
    queryFn: () => api.get<{ clientId: string; unread: number }[]>('/notifications/clients-unread'),
    enabled: canRead,
    refetchInterval: 120000,
  });
  if (!canRead) return null;
  const rows = clients.data?.filter((c) => c.status !== 'archived') ?? [];
  return (
    <section className="mt-5" aria-label="Espaços dos clientes">
      {!compact ? (
        <div className="mb-2 flex items-center justify-between px-2.5">
          <Link to="/clients" onClick={onNavigate} className="text-xs text-fg-subtle hover:text-fg">
            Clientes
          </Link>
          {canWrite && (
            <Button
              size="icon-sm"
              variant="ghost"
              aria-label="Criar cliente"
              onClick={() => setCreating(true)}
            >
              <Plus className="size-3.5" />
            </Button>
          )}
        </div>
      ) : (
        <div className="mx-auto mb-3 h-px w-5 bg-line" />
      )}
      {clients.isPending ? (
        <div className="space-y-2 px-2">
          {[0, 1, 2].map((n) => (
            <Skeleton key={n} className="h-9" />
          ))}
        </div>
      ) : clients.isError ? (
        <Button size="sm" variant="ghost" aria-label="Recarregar clientes" onClick={() => clients.refetch()}>
          <RefreshCw />
          {!compact && 'Tentar novamente'}
        </Button>
      ) : (
        <ul className="space-y-1">
          {rows.map((c) => {
            const selected = active.clientId === c.id;
            const open = expanded[c.id] ?? selected;
            const unread = counts.data?.find((n) => n.clientId === c.id)?.unread ?? 0;
            const to = `/clients/${c.id}`;
            const link = (
              <NavLink
                to={to}
                end
                onClick={onNavigate}
                aria-current={selected ? 'page' : undefined}
                className={cn(
                  'relative flex min-w-0 flex-1 items-center gap-2.5 rounded-control py-2 text-[13px] transition-colors motion-reduce:transition-none',
                  compact ? 'w-10 justify-center' : 'pl-2 pr-1',
                  selected ? 'bg-raised text-fg' : 'text-fg-muted hover:bg-hover hover:text-fg',
                )}
              >
                <ClientMark
                  name={c.name}
                  color={c.color}
                  logoUrl={c.logoUrl}
                  size="sm"
                  className="size-6 rounded-[7px] text-[10px]"
                />
                {!compact && <span className="min-w-0 flex-1 truncate">{c.name}</span>}
                {unread > 0 && (
                  <>
                    <span
                      aria-hidden
                      className={cn(
                        'flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-full bg-signal px-1 text-[10px] font-semibold text-signal-fg',
                        compact && 'absolute -top-1 -right-1',
                      )}
                    >
                      {unread > 99 ? '99+' : unread}
                    </span>
                    <span className="sr-only">{unread} notificações não lidas</span>
                  </>
                )}
              </NavLink>
            );
            return (
              <li key={c.id}>
                <div className={cn('flex items-center gap-0.5 rounded-control', selected && 'bg-raised')}>
                  {compact ? (
                    <Tooltip label={`${c.name}${unread ? ` · ${unread} notificações` : ''}`} side="right">
                      {link}
                    </Tooltip>
                  ) : (
                    link
                  )}
                  {!compact && (
                    <button
                      type="button"
                      aria-label={`${open ? 'Recolher' : 'Expandir'} ${c.name}`}
                      aria-expanded={open}
                      onClick={() => setExpanded((s) => ({ ...s, [c.id]: !open }))}
                      className="shrink-0 rounded-control p-2 text-fg-subtle hover:bg-hover hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info"
                    >
                      <ChevronDown
                        className={cn(
                          'size-3.5 transition-transform motion-reduce:transition-none',
                          !open && '-rotate-90',
                        )}
                      />
                    </button>
                  )}
                </div>
                {!compact && open && (
                  <ul className="mt-1 mb-2 ml-5 border-l border-line pl-3">
                    {[
                      { path: 'projects', label: 'Projetos', show: true },
                      { path: 'creative/boards', label: 'Creative', show: canCreative },
                      { path: 'inbox', label: 'Inbox', show: true },
                      { path: 'requests', label: 'Pedidos', show: true },
                      { path: 'access', label: 'Acessos', show: canInvite },
                    ]
                      .filter((x) => x.show)
                      .map((x) => (
                        <li key={x.path}>
                          <NavLink
                            to={`${to}/${x.path}`}
                            onClick={onNavigate}
                            className={({ isActive }) =>
                              cn(
                                'flex h-8 items-center rounded-[8px] px-2 text-xs transition-colors',
                                isActive || (selected && active.section === x.path.split('/')[0])
                                  ? 'bg-hover text-fg'
                                  : 'text-fg-subtle hover:bg-hover hover:text-fg',
                              )
                            }
                          >
                            {x.label}
                          </NavLink>
                        </li>
                      ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {!clients.isPending && !clients.isError && !rows.length && !compact && (
        <p className="px-2.5 py-3 text-xs leading-relaxed text-fg-subtle">
          Seus clientes aparecerão aqui assim que forem cadastrados.
        </p>
      )}
      {canWrite && <ClientFormDialog open={creating} onOpenChange={setCreating} />}
    </section>
  );
}
